import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Navbar from "../Navbar";
import "./Boost.css";
import {
  BOOST_MAX_VIEWS,
  BOOST_PRICE,
  formatDate,
  formatTime,
  getBoostStatus,
} from "../../services/boostApi";

// =====================================================
// BLOG BOOST SUCCESSFUL  —  /boost/:blogId/success
//
// Shown once the payment has been verified and the campaign activated.
// The receipt is handed over in navigation state, but the page also reads
// the campaign back from the server, so refreshing or printing later still
// shows the real, current record rather than a blank screen.
//
// Close opens Boost Analytics.
// =====================================================
function BoostSuccess() {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const passed = location.state || {};
  const [blog, setBlog] = useState(passed.blog || null);
  const [boost, setBoost] = useState(passed.boost || null);
  const [loading, setLoading] = useState(!passed.boost);

  const refresh = useCallback(async () => {
    try {
      const data = await getBoostStatus(blogId);
      if (data?.blog) setBlog(data.blog);
      if (data?.boost) setBoost(data.boost);
    } catch {
      // The receipt already on screen stays as it is.
    } finally {
      setLoading(false);
    }
  }, [blogId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const goToAnalytics = () => navigate(`/boost/${blogId}/analytics`, { replace: true });

  if (loading && !boost) {
    return (
      <div className="boost-page">
        <Navbar />
        <div className="boost-container">
          <div className="boost-loading">
            <div className="boost-spinner" />
            <p>Loading your receipt…</p>
          </div>
        </div>
      </div>
    );
  }

  const startedAt = boost?.startedAt;
  const status = boost?.isActive ? "Active" : "Completed";

  return (
    <div className="boost-page">
      <div className="boost-no-print">
        <Navbar />
      </div>

      <div className="boost-container">
        <div className="boost-card boost-receipt" id="boost-receipt">
          <div className="boost-success-icon">✓</div>
          <p className="boost-success-kicker">PAYMENT SUCCESSFUL</p>
          <h1 className="boost-success-title">Blog Boost Successful</h1>

          {blog?.title && <p className="boost-receipt-blog">“{blog.title}”</p>}

          <div className="boost-receipt-rows">
            <div className="boost-receipt-row">
              <span>Date</span>
              <strong>{formatDate(startedAt)}</strong>
            </div>
            <div className="boost-receipt-row">
              <span>Time</span>
              <strong>{formatTime(startedAt)}</strong>
            </div>
            <div className="boost-receipt-row">
              <span>Paid Amount</span>
              <strong>₹{boost?.amount ?? BOOST_PRICE}</strong>
            </div>
            <div className="boost-receipt-row">
              <span>Maximum Users</span>
              <strong>{boost?.maxViews ?? BOOST_MAX_VIEWS}</strong>
            </div>
            <div className="boost-receipt-row">
              <span>Current Status</span>
              <strong className={boost?.isActive ? "boost-text-active" : "boost-text-done"}>
                {status} · {boost?.uniqueViews ?? 0}/{boost?.maxViews ?? BOOST_MAX_VIEWS} users
              </strong>
            </div>
            {boost?.paymentId && (
              <div className="boost-receipt-row">
                <span>Payment ID</span>
                <strong className="boost-receipt-mono">{boost.paymentId}</strong>
              </div>
            )}
          </div>

          <p className="boost-receipt-note">
            Your blog now appears at the top of Read Blogs for readers who have not seen it yet.
            The boost stops automatically after {boost?.maxViews ?? BOOST_MAX_VIEWS} different
            users.
          </p>
        </div>

        <div className="boost-actions boost-no-print">
          <button type="button" className="boost-secondary-btn" onClick={() => window.print()}>
            🖨️ Print
          </button>
          <button type="button" className="boost-pay-btn" onClick={goToAnalytics}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default BoostSuccess;
